import { Button, Stack, Text } from '@adc/parallax-component-library';
import { IconPlus } from '@adc/parallax-icons';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import type { Department } from '~/api/types';
import { DepartmentTable } from '~/components/table/department/DepartmentTable';
import type { DepartmentTableColumnKey } from '~/components/table/department/types';
import { USER_ROLES } from '~/features/auth/roles';
import { useModal } from '~/features/modals/useModal';
import { Route as DepartmentDetailsRoute } from '~/routes/hcp-admin/departments.$departmentId';
import type { DepartmentSearchParams } from '~/types/DepartmentRoute';
import { buildTestId } from '~/utils/buildTestId';
import { protectedRoute } from '~/utils/routesAuthorization';

const TEST_ID = 'departments-table';

export const Route = createFileRoute('/hcp-admin/departments/')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): DepartmentSearchParams => {
    // Validate and parse the search params into a typed state
    const defaultSortDir = search.sortDir === 'asc' ? 'asc' : 'desc';
    const sortDir = search.sortDir ? defaultSortDir : undefined;

    return {
      page: search.page ? Number(search.page) || 1 : undefined,
      sort: (search.sort as DepartmentTableColumnKey | undefined) ?? undefined,
      search: search.search ? decodeURI(search.search as string) : undefined,
      sortDir,
    };
  },
  beforeLoad: () => {
    protectedRoute({
      roles: [USER_ROLES.HcpAdmin, USER_ROLES.NonHcpAdmin],
    });
  },
});

function RouteComponent() {
  const {
    page = 1,
    search = '',
    sort = '',
    sortDir = 'desc',
  } = Route.useSearch();

  const navigate = useNavigate({ from: Route.fullPath });

  const { openModal } = useModal();

  const handleSearch = async (searchValue: string) => {
    // Update the search term and reset to the first page
    await navigate({
      search: (prev) => ({ ...prev, page: 1, search: encodeURI(searchValue) }),
    });
  };

  const handlePageChange = async (newPage: number) => {
    // Update the page number in the search params
    await navigate({
      search: (prev) => ({ ...prev, page: newPage }),
    });
  };

  const handleSort = async (columnKey: DepartmentTableColumnKey) => {
    // Update the sort column and direction in the search params
    await navigate({
      search: (prev) => ({
        ...prev,
        sort: columnKey,
        sortDir: sort === columnKey && sortDir === 'asc' ? 'desc' : 'asc',
      }),
    });
  };

  const onRowPress = async (department: Department) => {
    await navigate({
      to: DepartmentDetailsRoute.fullPath,
      params: { departmentId: department.departmentId },
    });
  };

  return (
    <Stack height="auto" testID={TEST_ID}>
      <Text
        type="$h3.medium"
        marginBottom="$2"
        testID={buildTestId({ testID: TEST_ID, suffix: 'title' })}
      >
        Department & Settings
      </Text>
      <DepartmentTable
        testID={TEST_ID}
        page={page}
        search={search}
        sort={sort}
        sortDir={sortDir}
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        onSortChange={handleSort}
        onRowPress={onRowPress}
        headerActions={
          <Button
            testID={buildTestId({
              testID: TEST_ID,
              suffix: ['add', 'department', 'button'],
            })}
            size="medium"
            variant="primary"
            label="Add Department"
            iconStart={<IconPlus />}
            onPress={() => {
              openModal('DepartmentCreate', {
                testID: 'department-create-modal',
              });
            }}
          />
        }
      />
    </Stack>
  );
}
